/**
 * CSV parsing for the alumni console's client import (ATH-267). The cell splitting and header
 * matching are `./csv`'s.
 *
 * The API is the authority on every row: it re-checks the MR number and email, skips duplicates
 * against everyone already in the system, and flags a value it cannot store. This exists so a
 * coordinator sees the problems in the file before anything is sent. MR numbers go through the same
 * `normalizeMrNumber` the API uses, so a number this calls readable, the API reads the same way.
 */

import type { CreateClientInput } from "@athena/common/graphql/generated/schema";
import { CREATE_CLIENTS_MAX_ROWS } from "@athena/common/utils/clientImport";
import { normalizeMrNumber } from "@athena/common/utils/mrNumber";
import { findColumn, normalizeHeader, parseCsv } from "./csv";

type ClientField = keyof CreateClientInput;

/** Header spellings accepted per field. `findColumn` ignores case, underscores and spaces. */
const HEADERS: Record<ClientField, readonly string[]> = {
    mrNumber: [
        "mr number",
        "mr",
        "mrn",
        "mr #",
        "medical record number",
    ],
    email: [
        "email",
        "email address",
        "e-mail",
    ],
    firstName: [
        "first name",
        "first",
    ],
    lastName: [
        "last name",
        "last",
    ],
    phoneNumber: [
        "phone",
        "phone number",
        "mobile",
        "cell",
    ],
    dateOfBirth: [
        "date of birth",
        "dob",
        "birth date",
        "birthday",
    ],
    location: ["location"],
    program: ["program"],
    dischargeDate: [
        "discharge date",
    ],
    dischargeType: [
        "discharge type",
    ],
    soberDate: [
        "sober date",
        "sobriety date",
    ],
    sex: ["sex"],
    genderIdentity: [
        "gender identity",
        "gender",
    ],
    address: ["address"],
    emergencyContactName: [
        "emergency contact name",
        "emergency contact",
    ],
    emergencyContactPhone: [
        "emergency contact phone",
    ],
    emergencyContactRelationship: [
        "emergency contact relationship",
    ],
};

const FIELDS = Object.keys(HEADERS) as ClientField[];

/** Deliberately loose — the server's validator is the authority. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Why a row cannot be sent. Compared by the page to choose its message, so named once here. */
export const CLIENT_IMPORT_ROW_PROBLEMS = {
    MISSING_MR_NUMBER: "MISSING_MR_NUMBER",
    INVALID_MR_NUMBER: "INVALID_MR_NUMBER",
    /** An earlier row in the file has the same MR number, however spelled. */
    DUPLICATE_MR_NUMBER_IN_FILE: "DUPLICATE_MR_NUMBER_IN_FILE",
    MISSING_EMAIL: "MISSING_EMAIL",
    INVALID_EMAIL: "INVALID_EMAIL",
    /** An earlier row in the file has the same email. */
    DUPLICATE_EMAIL_IN_FILE: "DUPLICATE_EMAIL_IN_FILE",
} as const;
export type ClientImportRowProblem = (typeof CLIENT_IMPORT_ROW_PROBLEMS)[keyof typeof CLIENT_IMPORT_ROW_PROBLEMS];

/** Why nothing in the file can be sent. */
export const CLIENT_IMPORT_FILE_PROBLEMS = {
    /** No header row, or a header and nothing under it. */
    NO_ROWS: "NO_ROWS",
    /** No MR number or no email column — see `missingColumns`. */
    MISSING_COLUMNS: "MISSING_COLUMNS",
    /** More rows than one import takes (`CREATE_CLIENTS_MAX_ROWS`). */
    TOO_MANY_ROWS: "TOO_MANY_ROWS",
} as const;
export type ClientImportFileProblem = (typeof CLIENT_IMPORT_FILE_PROBLEMS)[keyof typeof CLIENT_IMPORT_FILE_PROBLEMS];

const REQUIRED_FIELDS = [
    "mrNumber",
    "email",
] as const satisfies readonly ClientField[];

export interface ClientImportRow {
    /** 1-based line in the file, for pointing at the offending row. */
    line: number;
    /** What is sent: blanks as null, the MR number in its one spelling when it could be read. */
    input: CreateClientInput;
    /** Set when this row can't be sent; it is left out of the request. */
    problem?: ClientImportRowProblem;
}

export interface ClientImportFile {
    rows: ClientImportRow[];
    /** A problem with the file as a whole: nothing is sendable. */
    problem?: ClientImportFileProblem;
    /** With MISSING_COLUMNS: which of the required columns were not found. */
    missingColumns?: (typeof REQUIRED_FIELDS)[number][];
    /**
     * Headers that matched no field, as written. Shown, because a column that is not recognized is
     * not read at all — a misspelled "Sobriety Dte" would drop every sober date without a word.
     */
    ignoredColumns: string[];
}

/**
 * Columns read into a field rather than being one (ATH-244): KIPU's export gives a full name and an
 * address in parts. Each is used only when the file has no column for the field itself.
 */
const FULL_NAME_HEADERS = [
    "full name",
    "name",
    "client name",
    "patient name",
] as const;
const ADDRESS_PART_HEADERS = {
    street: [
        "address street",
        "street",
        "address line 1",
        "address 1",
    ],
    street2: [
        "address street2",
        "address street 2",
        "address line 2",
        "address 2",
    ],
    city: [
        "address city",
        "city",
    ],
    state: [
        "address state",
        "state",
    ],
    zip: [
        "address zip",
        "zip",
        "zip code",
        "postal code",
    ],
} as const;

/**
 * A full name as first and last: the last word is the last name and the rest the first, so "Mary
 * Ann Smith" is Mary Ann / Smith. "Smith, Mary Ann" is read the other way round.
 */
export const splitFullName = (fullName: string): { firstName: string | null; lastName: string | null } => {
    const [
        before,
        after,
    ] = fullName.split(",").map((part) => part.trim().replace(/\s+/g, " "));
    if (after) return { firstName: after, lastName: before || null };
    const words = (before ?? "").split(" ").filter(Boolean);
    if (words.length <= 1) return { firstName: words[0] ?? null, lastName: null };
    return { firstName: words.slice(0, -1).join(" "), lastName: words[words.length - 1] ?? null };
};

/** An address from its parts, written the way the KIPU sync writes one: "street street2, city, ST zip". */
export const joinAddress = (parts: {
    street: string | null;
    street2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
}): string | null => {
    const street = [
        parts.street,
        parts.street2,
    ]
        .filter(Boolean)
        .join(" ");
    const cityLine = [
        parts.city,
        [
            parts.state,
            parts.zip,
        ]
            .filter(Boolean)
            .join(" "),
    ]
        .filter(Boolean)
        .join(", ");
    return (
        [
            street,
            cityLine,
        ]
            .filter(Boolean)
            .join(", ") || null
    );
};

/** Maps CSV text onto the rows `createClients` takes, and says which cannot be sent and why. */
export const parseClientImportCsv = (text: string): ClientImportFile => {
    const [
        headers,
        ...data
    ] = parseCsv(text);
    if (!headers || data.length === 0)
        return { rows: [], problem: CLIENT_IMPORT_FILE_PROBLEMS.NO_ROWS, ignoredColumns: [] };

    const columns = Object.fromEntries(
        FIELDS.map((field) => [
            field,
            findColumn(headers, HEADERS[field]),
        ]),
    ) as Record<ClientField, number>;
    const fullNameColumn = findColumn(headers, FULL_NAME_HEADERS);
    const addressColumns = Object.fromEntries(
        Object.entries(ADDRESS_PART_HEADERS).map(
            ([
                part,
                names,
            ]) => [
                part,
                findColumn(headers, names),
            ],
        ),
    ) as Record<keyof typeof ADDRESS_PART_HEADERS, number>;
    const used = new Set<number>([
        ...Object.values(columns),
        fullNameColumn,
        ...Object.values(addressColumns),
    ]);
    const ignoredColumns = headers.filter((header, index) => !used.has(index) && normalizeHeader(header) !== "");

    const missingColumns = REQUIRED_FIELDS.filter((field) => columns[field] < 0);
    if (missingColumns.length > 0) {
        return { rows: [], problem: CLIENT_IMPORT_FILE_PROBLEMS.MISSING_COLUMNS, missingColumns, ignoredColumns };
    }
    if (data.length > CREATE_CLIENTS_MAX_ROWS) {
        return { rows: [], problem: CLIENT_IMPORT_FILE_PROBLEMS.TOO_MANY_ROWS, ignoredColumns };
    }

    const rows = data.map((cells, index): Pick<ClientImportRow, "line" | "input"> => {
        const cellAt = (column: number): string | null => {
            const cell = column < 0 ? "" : (cells[column] ?? "").trim();
            return cell === "" ? null : cell;
        };
        const value = (field: ClientField): string | null => cellAt(columns[field]);
        const input = Object.fromEntries(
            FIELDS.map((field) => [
                field,
                value(field),
            ]),
        ) as CreateClientInput;
        input.mrNumber = input.mrNumber ?? "";
        input.email = input.email ?? "";
        const fullName = cellAt(fullNameColumn);
        if (fullName && !input.firstName && !input.lastName) Object.assign(input, splitFullName(fullName));
        input.address ??= joinAddress({
            street: cellAt(addressColumns.street),
            street2: cellAt(addressColumns.street2),
            city: cellAt(addressColumns.city),
            state: cellAt(addressColumns.state),
            zip: cellAt(addressColumns.zip),
        });
        return { line: index + 2, input }; // +1 for the header, +1 for 1-based lines.
    });

    return { rows: checkClientImportRows(rows), ignoredColumns };
};

/**
 * Says which rows can't be sent and why, and puts each readable MR number in its one spelling.
 *
 * Runs over the whole file at once, because a duplicate is one of an earlier row. The page runs it
 * again each time a coordinator corrects an MR number or email in place; a row it has already
 * checked comes out unchanged.
 */
export const checkClientImportRows = (rows: readonly Pick<ClientImportRow, "line" | "input">[]): ClientImportRow[] => {
    const seenMrNumbers = new Set<string>();
    const seenEmails = new Set<string>();
    return rows.map(({ line, input }): ClientImportRow => {
        const rawMrNumber = input.mrNumber.trim();
        const mrNumber = normalizeMrNumber(rawMrNumber);
        const email = input.email.trim();

        const problem = ((): ClientImportRowProblem | undefined => {
            if (!rawMrNumber) return CLIENT_IMPORT_ROW_PROBLEMS.MISSING_MR_NUMBER;
            if (!mrNumber) return CLIENT_IMPORT_ROW_PROBLEMS.INVALID_MR_NUMBER;
            if (!email) return CLIENT_IMPORT_ROW_PROBLEMS.MISSING_EMAIL;
            if (!LOOKS_LIKE_EMAIL.test(email)) return CLIENT_IMPORT_ROW_PROBLEMS.INVALID_EMAIL;
            if (seenMrNumbers.has(mrNumber)) return CLIENT_IMPORT_ROW_PROBLEMS.DUPLICATE_MR_NUMBER_IN_FILE;
            if (seenEmails.has(email.toLowerCase())) return CLIENT_IMPORT_ROW_PROBLEMS.DUPLICATE_EMAIL_IN_FILE;
            return undefined;
        })();
        // Only a row that will be sent claims its MR number and email: a second row repeating a
        // broken one is reported for its own problem, not as a duplicate of a row nobody sends.
        if (!problem) {
            seenMrNumbers.add(mrNumber as string);
            seenEmails.add(email.toLowerCase());
        }

        return { line, input: { ...input, mrNumber: mrNumber ?? rawMrNumber, email }, ...(problem && { problem }) };
    });
};

/**
 * The header row a coordinator downloads. Headers only: an example row would be imported as a real
 * client by anyone who uploaded the template before filling it in.
 */
export const CLIENT_IMPORT_TEMPLATE =
    "mr_number,email,first_name,last_name,phone,date_of_birth,location,program,discharge_date,discharge_type,sober_date,sex,gender_identity,address,emergency_contact_name,emergency_contact_phone,emergency_contact_relationship\n";
